/**
 * Helpers for turning assistant message markdown — which embeds
 * machine-directed blocks (```scrambl-cards / ```scrambl-followups fences,
 * scrambl:// links) — into something fit for human eyes outside the
 * ReactMarkdown renderer.
 */

import type { ScramblCard } from './ModuleCards'

const CARDS_FENCE = /```scrambl-cards\s*([\s\S]*?)```/g
const FOLLOWUPS_FENCE = /```scrambl-followups\s*[\s\S]*?```/g
const CANVAS_FENCE = /```scrambl-canvas\s*[\s\S]*?```/g
const VIEW_FENCE = /```scrambl-view\s*[\s\S]*?```/g

/**
 * While a response is still streaming, an unterminated ```scrambl-… fence
 * would dump raw JSON into the bubble until its closing fence arrives.
 * Truncate the visible content at such a fence; completed blocks are left
 * alone (the markdown renderer turns them into cards/chips as they finish).
 */
export function sanitizeStreamingContent(content: string): string {
  const fences = [...content.matchAll(/^```[^\n]*/gm)]
  if (fences.length % 2 === 0) return content

  const last = fences[fences.length - 1]
  const info = last[0].slice(3).trim().toLowerCase()
  // Hide the block even while its name is still streaming in ("scr",
  // "scrambl-car", …). Ordinary code fences keep streaming visibly.
  const isScramblBlock =
    info.length > 0 &&
    ('scrambl-cards'.startsWith(info) ||
      'scrambl-followups'.startsWith(info) ||
      info.startsWith('scrambl'))
  if (!isScramblBlock) return content

  return content.slice(0, last.index).trimEnd()
}

/**
 * Convert an assistant message to clean plain text for the clipboard:
 * card blocks become a readable bullet list, followup chips are dropped,
 * and scrambl:// deep links keep just their label.
 */
export function toClipboardText(content: string): string {
  // A trailing unterminated machine block (aborted stream) is noise either way.
  let result = sanitizeStreamingContent(content)

  result = result.replace(CARDS_FENCE, (match, body: string) => {
    try {
      const cards = JSON.parse(body.trim()) as ScramblCard[]
      if (Array.isArray(cards)) {
        return cards
          .map(card =>
            `- ${[card.identifier, card.name].filter(Boolean).join(' — ')}`,
          )
          .join('\n')
      }
    } catch {
      // Unparseable machine block — better to drop it than paste JSON.
    }
    return ''
  })

  result = result.replace(FOLLOWUPS_FENCE, '')
  result = result.replace(CANVAS_FENCE, '')
  result = result.replace(VIEW_FENCE, '')
  result = result.replace(
    /\[([^\]]+)\]\(scrambl(?:-help)?:\/\/[^)]*\)/g,
    '$1',
  )

  return result.replace(/\n{3,}/g, '\n\n').trim()
}
